import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import utilServices from 'app/services/util-services'
import { getClassById, selectItemById } from 'app/redux/store/features/classes'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import UpdateClassForm from './UpdateClassForm'
import termServices from 'app/services/pages/courses/setup/termServices/termServices'
import locationServices from 'app/services/pages/settings/definitions/locationServices/locationServices'
import CustomStore from 'devextreme/data/custom_store'
import DataSource from 'devextreme/data/data_source'
import classTypeServices from 'app/services/pages/settings/definitions/classTypeServices/classTypeServices'
import classesService from '../../../../../../../../services/pages/courses/setup/classesServices/classesService'
import ageGroupService from 'app/services/pages/settings/definitions/ageGroupServices/ageGroupServices'
import UpdateDetailImage from './UpdateDetailImage'
import UpdateSearchImage from './UpdateSearchImage'
import { useJumboTheme } from '@jumbo/hooks'
import { useMediaQuery, Box, IconButton, Stack } from '@mui/material'
import Div from '@jumbo/shared/Div'
import genderTypeServices from 'app/services/pages/settings/definitions/genderTypeServices/genderTypeServices'
import WestIcon from '@mui/icons-material/West'
import { createTeacherLookupCustomStore } from 'app/utils/teacherLookupCustomStore/teacherLookupCustomStore'

const UpdateClass = () => {
    const [tabTitle, setTabTitle] = React.useState('update-class')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { termConfig } = useSelector((state) => state.terms)
    const { enqueueSnackbar } = useSnackbar()
    const { state } = useLocation()
    const { classId } = state
    const { classes } = useSelector((state) => state.classes)
    const classData = selectItemById(classes, classId)
    const filePathSearchData = classData.filePathSearch
    const filePathDetailData = classData.filePathDetail
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))
    const md = useMediaQuery(theme.breakpoints.down('md'))
    const min = sm || md
    const [selectBoxLoading, setSelectBoxLoading] = useState({
        termLoading: false,
        locationLoading: false,
        classTypeLoading: false,
        classGenderTypeLoading: false,
        ageGroupLoading: false,
        prvClassIdLoading: false,
        teacherLoading: false,
    })

    const handleError = useCallback(
        (error) => {
            if (error.status === '401') {
                navigate('/profile/signout')
            } else if (error.detail) {
                enqueueSnackbar(error.detail, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            } else {
                enqueueSnackbar(error, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            }
        },
        [enqueueSnackbar, navigate]
    )

    const handleErrorRef = React.useRef(handleError)
    handleErrorRef.current = handleError

    useEffect(() => {
        if (utilServices.isNullOrUndefined(classData.id)) {
            dispatch(getClassById({ id: classId }))
        }
    }, [])

    const onTabSelectionChanged = (e, newValue) => {
        setTabTitle(newValue)
    }

    const genderTypeLookupList = useCallback(async (searchTerm) => {
        try {
            return await genderTypeServices.getGenderTypeLookupList({ genderTypeName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const genderTypeLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await genderTypeLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, classGenderTypeLoading: true }))

                        try {
                            return await genderTypeServices.getGenderTypeLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, classGenderTypeLoading: false }))
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const termLookupList = useCallback(async (searchTerm) => {
        try {
            return await termServices.getTermLookupList({ termYear: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await termLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, termLoading: true }))

                        try {
                            return await termServices.getTermLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, termLoading: false }))
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const classTypeLookupList = useCallback(async (searchTerm) => {
        try {
            return await classTypeServices.getClassTypeLookupList({ classTypeName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classTypeLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await classTypeLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, classTypeLoading: true }))

                        try {
                            return await classTypeServices.getClassTypeLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, classTypeLoading: false }))
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const locationLookupList = useCallback(async (searchTerm) => {
        try {
            return await locationServices.getLocationLookupList({ locationName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const locationLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await locationLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, locationLoading: true }))

                        try {
                            return await await locationServices.getLocationById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, locationLoading: false }))
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const classLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(termConfig?.termId)) return utilServices.emptyList()
                return await classesService.getPrvTermClassLookupList({
                    termId: termConfig.termId,
                    className: searchTerm,
                })
            } catch (error) {
                handleError(error)
            }
        },
        [termConfig.termId]
    )

    const classLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await classLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, prvClassIdLoading: true }))
                        try {
                            return await classesService.getClassLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, prvClassIdLoading: false }))
                        }
                    },
                    errorHandler: function (e) {
                        console.log(e)
                    },
                }),
                reshapeOnPush: true,
            }),
        [termConfig.termId]
    )

    /** Primary SelectBox: only the main teacher id is merged into lookup/list. */
    const buildPrimaryTeacherIncludeIds = useCallback((data) => {
        if (data?.teacherId == null || data.teacherId === '') return []
        return [String(data.teacherId)]
    }, [])

    /** Additional TagBox: only additional ids (same rules as save — never primary). */
    const buildAdditionalTeacherIncludeIds = useCallback((data) => {
        if (!Array.isArray(data?.additionalTeacherIds)) return []
        return utilServices.sanitizeAdditionalTeacherIds(data, data.additionalTeacherIds)
    }, [])

    const teacherPrimaryIncludeIdsRef = React.useRef([])
    teacherPrimaryIncludeIdsRef.current = buildPrimaryTeacherIncludeIds(classData)

    const teacherAdditionalIncludeIdsRef = React.useRef([])
    teacherAdditionalIncludeIdsRef.current = buildAdditionalTeacherIncludeIds(classData)

    const teacherPrimarySignature = useMemo(
        () => `${classData?.id ?? ''}|${classData?.teacherId ?? ''}`,
        [classData?.id, classData?.teacherId]
    )

    const teacherAdditionalSignature = useMemo(() => {
        const key = Array.isArray(classData?.additionalTeacherIds)
            ? [...classData.additionalTeacherIds].map(String).sort().join(',')
            : ''
        return `${classData?.id ?? ''}|${key}`
    }, [classData?.id, classData?.additionalTeacherIds])

    const teacherPrimaryLoadCacheRef = React.useRef(new Map())
    const teacherPrimaryLoadInFlightRef = React.useRef(new Map())
    const teacherAdditionalLoadCacheRef = React.useRef(new Map())
    const teacherAdditionalLoadInFlightRef = React.useRef(new Map())

    const teacherPrimaryStoreRef = React.useRef(null)
    if (!teacherPrimaryStoreRef.current) {
        teacherPrimaryStoreRef.current = createTeacherLookupCustomStore({
            getIncludeIds: () => teacherPrimaryIncludeIdsRef.current,
            loadCacheRef: teacherPrimaryLoadCacheRef,
            loadInFlightRef: teacherPrimaryLoadInFlightRef,
            getHandleError: () => handleErrorRef.current,
        })
    }

    const teacherAdditionalStoreRef = React.useRef(null)
    if (!teacherAdditionalStoreRef.current) {
        teacherAdditionalStoreRef.current = createTeacherLookupCustomStore({
            getIncludeIds: () => teacherAdditionalIncludeIdsRef.current,
            loadCacheRef: teacherAdditionalLoadCacheRef,
            loadInFlightRef: teacherAdditionalLoadInFlightRef,
            getHandleError: () => handleErrorRef.current,
        })
    }

    const teacherLookupsStorePrimary = useMemo(
        () => new DataSource({ store: teacherPrimaryStoreRef.current, reshapeOnPush: true }),
        []
    )
    const teacherLookupsStoreTagBox = useMemo(
        () => new DataSource({ store: teacherAdditionalStoreRef.current, reshapeOnPush: true }),
        []
    )

    useEffect(() => {
        teacherPrimaryLoadCacheRef.current.clear()
        teacherPrimaryLoadInFlightRef.current.clear()
        teacherLookupsStorePrimary.reload()
    }, [teacherPrimarySignature, teacherLookupsStorePrimary])

    useEffect(() => {
        teacherAdditionalLoadCacheRef.current.clear()
        teacherAdditionalLoadInFlightRef.current.clear()
        teacherLookupsStoreTagBox.reload()
    }, [teacherAdditionalSignature, teacherLookupsStoreTagBox])

    const teacherEditorOptions = useMemo(
        () => ({
            dataSource: teacherLookupsStoreTagBox,
            valueExpr: 'id',
            searchEnabled: true,
            searchTimeout: 300,
            minSearchLength: 1,
            showDataBeforeSearch: true,
            showClearButton: true,
            hideSelectedItems: false,
            // Ensure checkbox clicks apply immediately (important for remote lookups).
            applyValueMode: 'instantly',
            itemTemplate: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
            displayExpr: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
        }),
        [teacherLookupsStoreTagBox]
    )

    const ageGroupLookupList = useCallback(async (searchTerm) => {
        try {
            return await ageGroupService.getAgeGroupLookupList({ ageGroupName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const ageGroupLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await ageGroupLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, ageGroupLoading: true }))

                        try {
                            return await ageGroupService.getAgeGroupLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, ageGroupLoading: false }))
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/courses/setup/classes`, {
                                    state: { navigateFrom: 'cancel-update-class' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Update Class Item
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <Tabs
                    value={tabTitle}
                    onChange={onTabSelectionChanged}
                    indicatorColor='primary'
                    textColor='primary'
                    variant='scrollable'
                    scrollButtons='auto'
                    allowScrollButtonsMobile
                    sx={{
                        mt: -0.7,
                        bgcolor: 'background.default',
                        '.MuiTabs-scrollButtons': {
                            // Styling for scroll buttons
                            '&.Mui-disabled': {
                                opacity: 0.3, // Ensure disabled buttons are visible but faded
                            },
                        },
                    }}>
                    <Tab
                        label='Update Class'
                        value='update-class'
                        sx={{ width: min ? '125px' : '160px', textTransform: 'capitalize' }}
                    />
                    <Tab
                        label='Search Image'
                        value='search-image'
                        sx={{ width: min ? '125px' : '160px', textTransform: 'capitalize' }}
                    />
                    <Tab
                        label='Detail Image'
                        value='detail-image'
                        sx={{ width: min ? '125px' : '160px', textTransform: 'capitalize' }}
                    />
                </Tabs>
                <Div>
                    {tabTitle === 'update-class' && (
                        <Box paddingLeft={2} paddingRight={2} paddingTop={2}>
                            <UpdateClassForm
                                classData={classData}
                                teacherLookupsStorePrimary={teacherLookupsStorePrimary}
                                teacherEditorOptions={teacherEditorOptions}
                                classTypeLookupsStore={classTypeLookupsStore}
                                classLookupsStore={classLookupsStore}
                                locationLookupStore={locationLookupStore}
                                termLookupsStore={termLookupsStore}
                                selectBoxLoading={selectBoxLoading}
                                genderTypeLookupStore={genderTypeLookupStore}
                                ageGroupLookupStore={ageGroupLookupStore}
                                handleError={handleError}
                            />
                        </Box>
                    )}
                </Div>
                <Div>
                    {tabTitle === 'search-image' && (
                        <Box paddingLeft={2} paddingRight={2}>
                            <UpdateSearchImage filePathSearchData={filePathSearchData} />
                        </Box>
                    )}
                </Div>
                <Div>
                    {tabTitle === 'detail-image' && (
                        <Box paddingLeft={2} paddingRight={2}>
                            <UpdateDetailImage filePathDetailData={filePathDetailData} />
                        </Box>
                    )}
                </Div>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default UpdateClass
